import { useEffect, useRef, useState } from "react";
import {
    AnimatePresence,
    motion,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];
const lineWrap = { display: "block", overflow: "hidden" };

const EXAMPLES = [
    {
        tab: "01 · CRM REVIEW",
        title: "CRM Purchase Review",
        stamp: "NOT YET CLEARED",
        stampClass: "",
        rows: [
            ["01", <>Contract has a <b>12-month lock-in</b> with no early exit clause.</>],
            ["02", <>Data migration <b>not included</b> — quoted separately, cost unclear.</>],
            ["03", <>No confirmed integration with existing <b>invoicing system</b>.</>],
        ],
        foot: ["3 of 5 findings shown", "REVIEWED IN 3 DAYS"],
    },
    {
        tab: "02 · UAT GO-LIVE",
        title: "Pre-Go-Live UAT Report",
        stamp: "SIGN-OFF GRANTED",
        stampClass: "stamp-forest",
        rows: [
            ["01", <>All <b>14 defects</b> resolved and retested.</>],
            ["02", <>Invoicing module passes <b>GST rounding</b> checks.</>],
            ["03", <>Staff training completed across <b>2 sites</b>.</>],
        ],
        foot: ["Ready for launch", "SIGNED OFF IN 1 WEEK"],
    },
    {
        tab: "03 · AI READINESS",
        title: "AI Implementation Review",
        stamp: "RISK REVIEW",
        stampClass: "",
        rows: [
            ["01", <>Customer data reaches an <b>external model</b> without a consent check.</>],
            ["02", <>No <b>rollback plan</b> if AI outputs are wrong.</>],
            ["03", <>Pilot limited to <b>2 workflows</b> — scope is sensible.</>],
        ],
        foot: ["3 of 6 checks shown", "REVIEWED IN 2 DAYS"],
    },
    {
        tab: "04 · REQUIREMENTS REVIEW",
        title: "Requirements Definition Document",
        stamp: "GAPS FLAGGED",
        stampClass: "",
        rows: [
            ["01", <>Requirements cover <b>18 must-haves</b> across sales, ops and finance.</>],
            ["02", <>Vendor proposal meets <b>15 of 18</b> — gaps flagged for negotiation.</>],
            ["03", <>Reporting and <b>Xero integration</b> confirmed in writing before sign-off.</>],
        ],
        foot: ["3 of 18 requirements shown", "DRAFTED IN 2 WEEKS"],
    },
    {
        tab: "05 · AI ASSISTANT REVIEW",
        title: "AI Assistant Pilot Review",
        stamp: "APPROVED WITH GUARDRAILS",
        stampClass: "stamp-forest",
        rows: [
            ["01", <>Assistant answers <b>customer FAQs</b> with a human hand-off path.</>],
            ["02", <>No <b>customer data</b> leaves your systems — consent confirmed.</>],
            ["03", <>Weekly accuracy <b>spot checks</b> assigned to a named owner.</>],
        ],
        foot: ["3 of 5 checks shown", "REVIEWED IN 2 DAYS"],
    },
    {
        tab: "06 · AUTOMATION REVIEW",
        title: "Process Automation Review",
        stamp: "GUARDRAILS NEEDED",
        stampClass: "",
        rows: [
            ["01", <>Invoice entry <b>cut from 3 days to 2 hours</b> — time saved verified.</>],
            ["02", <>No <b>manual override</b> when the automation misfires.</>],
            ["03", <>Vendor's <b>per-run pricing</b> could overtake licence costs at scale.</>],
        ],
        foot: ["3 of 7 findings shown", "REVIEWED IN 3 DAYS"],
    },
];

export default function Hero() {
    const heroRef = useRef(null);
    const deckRef = useRef(null);
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const docY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const docR = useTransform(scrollYProgress, [0, 1], [0, -3]);

    const rx = useSpring(0, { stiffness: 120, damping: 18 });
    const ry = useSpring(0, { stiffness: 120, damping: 18 });

    const onMouseMove = (e) => {
        const r = deckRef.current?.getBoundingClientRect();
        if (!r) return;
        ry.set(((e.clientX - r.left) / r.width - 0.5) * 6);
        rx.set(-((e.clientY - r.top) / r.height - 0.5) * 4);
    };
    const resetTilt = () => {
        rx.set(0);
        ry.set(0);
    };

    useEffect(() => {
        if (paused) return;
        const t = setInterval(
            () => setActive((a) => (a + 1) % EXAMPLES.length),
            6500,
        );
        return () => clearInterval(t);
    }, [paused]);

    const go = (e, id) => {
        e.preventDefault();
        scrollToSection(id);
    };

    const example = EXAMPLES[active];

    return (
        <section className="hero" ref={heroRef}>
            <div className="wrap hero-grid">
                <div className="hero-copy">
                    <motion.div
                        className="eyebrow-line"
                        data-testid="hero-eyebrow"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                    >
                        Software Advisory | Requirements Elaboration |
                        Technology Evaluation | Decision Reports | UAT | AI
                        Transformation
                    </motion.div>
                    <h1 data-testid="hero-headline">
                        <span style={lineWrap}>
                            <motion.span
                                className="hero-line"
                                initial={{ y: "112%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 1.1,
                                    ease: EASE,
                                    delay: 0.22,
                                }}
                            >
                                Right <em>Requirements.</em>
                            </motion.span>
                        </span>
                        <span style={lineWrap}>
                            <motion.span
                                className="hero-line"
                                initial={{ y: "112%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 1.1,
                                    ease: EASE,
                                    delay: 0.36,
                                }}
                            >
                                Right <em>Technology.</em>
                            </motion.span>
                        </span>
                        <span style={lineWrap}>
                            <motion.span
                                className="hero-line"
                                initial={{ y: "112%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 1.1,
                                    ease: EASE,
                                    delay: 0.5,
                                }}
                            >
                                Right <em>Decision.</em>
                            </motion.span>
                        </span>
                    </h1>
                    <motion.p
                        className="hero-sub"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.62 }}
                    >
                        <strong>
                            From Business Needs to Technology Transformation.
                        </strong>{" "}
                        We help you understand, evaluate, decide, test and
                        transform — with confidence.
                    </motion.p>
                    <motion.div
                        className="hero-actions"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.74 }}
                    >
                        <a
                            href="#contact"
                            className="btn btn-primary"
                            data-testid="hero-cta-book"
                            onClick={(e) => go(e, "#contact")}
                        >
                            Reach us
                        </a>
                        <a
                            href="#services"
                            className="btn btn-ghost"
                            data-testid="hero-cta-services"
                            onClick={(e) => go(e, "#services")}
                        >
                            See our services
                        </a>
                    </motion.div>
                    <motion.div
                        className="hero-illustration"
                        data-testid="hero-illustration"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
                    >
                        <img
                            src="/hero-illustration.png"
                            alt="Software advisory for Australian businesses — requirements, technology evaluation, decision reports, UAT and AI transformation"
                            width={998}
                            height={563}
                            loading="eager"
                        />
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 32, rotate: 2.5 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    transition={{ duration: 1, ease: EASE, delay: 0.5 }}
                >
                    <motion.div style={{ y: docY, rotate: docR }}>
                        <motion.div
                            className="sample-doc-deck"
                            ref={deckRef}
                            onMouseMove={onMouseMove}
                            onMouseLeave={resetTilt}
                            onMouseEnter={() => setPaused(true)}
                            onMouseOut={() => setPaused(false)}
                            data-testid="hero-doc-deck"
                            style={{
                                rotateX: rx,
                                rotateY: ry,
                                transformPerspective: 900,
                            }}
                        >
                            <div className="doc-ghost doc-ghost-a" />
                            <div className="doc-ghost doc-ghost-b" />

                            <AnimatePresence mode="wait">
                                <motion.div
                                    className="sample-doc"
                                    key={active}
                                    initial={{ opacity: 0, x: 32, rotate: 1.5 }}
                                    animate={{ opacity: 1, x: 0, rotate: 0 }}
                                    exit={{ opacity: 0, x: -32, rotate: -1.5 }}
                                    transition={{
                                        duration: 0.45,
                                        ease: EASE,
                                    }}
                                    data-testid="hero-findings-card"
                                >
                                    <div className="sample-doc-head">
                                        <div>
                                            <div className="label">
                                                FINDING SUMMARY — SAMPLE
                                            </div>
                                            <div className="doc-title">
                                                {example.title}
                                            </div>
                                        </div>
                                        <div
                                            className={`stamp ${example.stampClass}`}
                                        >
                                            {example.stamp}
                                        </div>
                                    </div>
                                    <div className="finding-rows">
                                        {example.rows.map(([num, text]) => (
                                            <div
                                                className="finding-row"
                                                key={num}
                                            >
                                                <div className="finding-num">
                                                    {num}
                                                </div>
                                                <div className="finding-text">
                                                    {text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="sample-doc-foot">
                                        <span>{example.foot[0]}</span>
                                        <span>{example.foot[1]}</span>
                                    </div>
                                </motion.div>
                            </AnimatePresence>

                            <div className="doc-tabs">
                                {EXAMPLES.map((ex, i) => (
                                    <button
                                        key={ex.tab}
                                        className={`doc-tab ${i === active ? "active" : ""}`}
                                        data-testid={`hero-doc-tab-${i}`}
                                        onClick={() => setActive(i)}
                                    >
                                        {ex.tab}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
